/**
 * `/verify-email` — two states behind one route.
 *
 * With a `?token=` it spends the link. Without one it offers to send another,
 * which is where the sign-up screen and a refused sign-in both point.
 *
 * The two states are separate components on purpose: the resend form needs a
 * captcha, the confirmation does not, and `useCaptcha` fetches a challenge the
 * moment it mounts. One component would either fetch a challenge nobody needs
 * on every emailed link, or need a conditional hook.
 *
 * Confirming is a POST issued by script, not a GET. Mail providers and
 * corporate security appliances prefetch links in messages; a GET that
 * confirmed the account would be spent by a scanner before the person ever
 * clicked, and they would land on "this link has already been used".
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, api } from "@/api/client";
import type { MailSentResponse } from "@/api/types";
import { CaptchaField, useCaptcha } from "@/components/Captcha";
import { Spinner } from "@/components/Spinner";
import { useSession } from "@/state/session";

function Confirming({ token }: { token: string }) {
  const { confirmEmail } = useSession();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [alreadyVerified, setAlreadyVerified] = useState(false);
  // React StrictMode runs effects twice in development. The second run would
  // present a link the first run already spent, and the person would be shown
  // "already used" for a link that worked.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      try {
        const { alreadyVerified: already } = await confirmEmail(token);
        if (already) setAlreadyVerified(true);
        else navigate("/home", { replace: true });
      } catch (cause) {
        setError(
          cause instanceof ApiError
            ? (cause.fieldMessage ?? cause.message)
            : "This confirmation link could not be used.",
        );
      }
    })();
  }, [confirmEmail, navigate, token]);

  if (error) {
    return (
      <div className="panel panel--narrow" style={{ marginTop: 80 }}>
        <h1 style={{ marginBottom: 6 }}>That link did not work</h1>
        <div className="alert alert--error" style={{ marginTop: 16 }}>{error}</div>
        <p style={{ color: "var(--paper-400)", fontSize: 13, lineHeight: 1.6 }}>
          Confirmation links work once and expire after 24 hours. Request a fresh one and it will
          arrive in a moment.
        </p>
        <Link className="btn btn--accent btn--block" to="/verify-email" style={{ marginTop: 8 }}>
          Send a new link
        </Link>
      </div>
    );
  }

  if (alreadyVerified) {
    return (
      <div className="panel panel--narrow" style={{ marginTop: 80 }}>
        <h1 style={{ marginBottom: 6 }}>Already confirmed</h1>
        <p style={{ color: "var(--paper-300)", fontSize: 14, lineHeight: 1.6 }}>
          This address is confirmed and the account is ready. Sign in to start watching.
        </p>
        <Link className="btn btn--accent btn--block" to="/signin" style={{ marginTop: 8 }}>
          Sign in
        </Link>
      </div>
    );
  }

  return <Spinner label="Confirming your account" />;
}

function ResendForm() {
  const captcha = useCaptcha("password-reset");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.anonymousPost<MailSentResponse>("/api/v1/auth/resend-verification", {
        email,
        ...captcha.fields(),
      });
      setSent(true);
    } catch (cause) {
      setError(
        cause instanceof ApiError ? (cause.fieldMessage ?? cause.message) : "Could not send the email",
      );
    } finally {
      setBusy(false);
      void captcha.reload();
    }
  }

  if (sent) {
    return (
      <div className="panel panel--narrow" style={{ marginTop: 80 }}>
        <h1 style={{ marginBottom: 6 }}>Check your email</h1>
        {/*
          Deliberately says nothing about whether an account exists. The API
          answers identically either way, and repeating that here is what keeps
          this form from being a way to test which addresses are registered.
        */}
        <p style={{ color: "var(--paper-300)", fontSize: 14, lineHeight: 1.6 }}>
          If <strong>{email}</strong> has an account waiting to be confirmed, a new link is on its
          way. It works once and expires in 24 hours.
        </p>
        <Link className="btn btn--ghost btn--block" to="/signin" style={{ marginTop: 18 }}>
          Back to sign in
        </Link>
      </div>
    );
  }

  return (
    <form className="panel panel--narrow" onSubmit={submit} style={{ marginTop: 80 }}>
      <h1 style={{ marginBottom: 6 }}>Confirm your email</h1>
      <p style={{ color: "var(--paper-400)", fontSize: 13, marginTop: 0, marginBottom: 22 }}>
        Lost the link, or has it expired? We will send another.
      </p>
      {error && <div className="alert alert--error">{error}</div>}
      <div className="field">
        <label htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>
      <CaptchaField state={captcha} />
      <button className="btn btn--accent btn--block" type="submit" disabled={busy}>
        {busy ? "Sending…" : "Send the link again"}
      </button>
      <p style={{ marginTop: 18, color: "var(--paper-400)", fontSize: 13 }}>
        <Link to="/signin" style={{ color: "var(--accent)" }}>Back to sign in</Link>
      </p>
    </form>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  return (
    <main className="page container" style={{ display: "grid", placeItems: "center" }}>
      {token ? <Confirming token={token} /> : <ResendForm />}
    </main>
  );
}
